import { cx } from '@linaria/core';
import { useRef, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { Link } from '@provider/navigation';
import { resets } from '@ui/css';
import { useLoaderState, CSSRefTransition } from '@utils/react';
import { tokens, iconsContainerCls, iconWrapperCls, iconTransitionDurationMs, label, checkmark, buttonCls, modifiers, errorCls, successCls, throbberCls } from './icon-button.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Tooltip } from '../overlays/tooltip/tooltip.js';
import { CheckmarkIcon } from '../../icons/checkmark.js';

/**
 * An icon button represents a button primarily visualised through an icon
 * used to trigger a specific action, often related to a parent component.
 *
 * As such this is often used in conjunction with list items/table rows which
 * represents interactable items.
 */
function IconButton({
  as,
  children,
  label: label$1,
  icon,
  iconKey,
  activeIcon,
  onClick,
  visibleLabel,
  active,
  tooltip,
  state: forcedState,
  size,
  color,
  className,
  async,
  ...props
}) {
  const internalRef = useRef(null);
  const buttonRef = props.ref ?? internalRef;
  const [internalState, setState] = useState("default");
  const state = forcedState || internalState;
  const showThrobber = useLoaderState(!!async?.showThrobberWhilePending && state === "pending", {
    delayMs: 250,
    durationMs: 375
  });
  const showThrobberNow = showThrobber.current;
  const handleClick = async evt => {
    setState("pending");
    try {
      await onClick?.(evt);
      setState("success");
    } catch {
      setState("error");
    } finally {
      setTimeout(() => {
        setState("default");
      }, 2500);
    }
  };
  const resolvedContentKey = (() => {
    if (showThrobberNow) {
      return "throbber";
    }
    if (async?.showStatesAfterResult && !visibleLabel && (state === "success" || state === "error")) {
      return state;
    }
    return activeIcon && active ? "active" : "default";
  })();
  const content = (() => {
    switch (resolvedContentKey) {
      case "default":
        return icon;
      case "active":
        return activeIcon;
      case "throbber":
        return /*#__PURE__*/jsx("span", {
          className: throbberCls
        });
      case "success":
        return /*#__PURE__*/jsx("span", {
          className: successCls,
          children: /*#__PURE__*/jsx(CheckmarkIcon, {
            height: 7.5
          })
        });
      case "error":
        return /*#__PURE__*/jsx("span", {
          className: errorCls
        });
    }
  })();
  const Comp = as === "a" ? Link : as || "button";
  return /*#__PURE__*/jsx(Tooltip, {
    containerRef: buttonRef,
    placement: tooltip?.placement ?? "top",
    disableFallbackPlacements: tooltip?.disableFallbackPlacements,
    body: tooltip?.body || label$1,
    ariaType: tooltip?.ariaType || "label",
    disabled: tooltip?.disabled || active || visibleLabel && !tooltip?.body
    // Negate padding used to extend clickable area
    ,
    offsetPx: tooltip?.offsetPx ?? 5,
    children: ref =>
    /*#__PURE__*/
    // @ts-expect-error We allow our button to contain a generic
    // HTMLElement.
    jsxs(Comp, {
      ...props,
      ref: ref,
      onClick: async ? handleClick : onClick,
      style: tokens.__override({
        color
      }),
      className: cx(resets.rootCls, className, buttonCls, size === "small" && modifiers.small.className, visibleLabel && modifiers.withLabel.className),
      "data-active": active,
      disabled: "disabled" in props && props.disabled || state === "pending",
      "data-state": async?.showStatesAfterResult ? state : "default",
      children: [/*#__PURE__*/jsx("span", {
        className: iconsContainerCls,
        children: /*#__PURE__*/jsx(TransitionGroup, {
          component: null,
          children: /*#__PURE__*/jsx(CSSRefTransition, {
            timeout: iconTransitionDurationMs,
            classNames: iconWrapperCls,
            children: ref => /*#__PURE__*/jsx("span", {
              ref: ref,
              className: cx(iconWrapperCls),
              children: content
            })
          }, resolvedContentKey === "default" ? iconKey || "default" : resolvedContentKey)
        })
      }), visibleLabel && /*#__PURE__*/jsxs("span", {
        className: label,
        children: [/*#__PURE__*/jsx("span", {
          children: label$1
        }), async?.showStatesAfterResult && state === "success" && /*#__PURE__*/jsx("span", {
          className: checkmark,
          children: /*#__PURE__*/jsx(CheckmarkIcon, {
            height: 7,
            strokeWidth: 3
          })
        })]
      }), children]
    })
  });
}

export { IconButton };
//# sourceMappingURL=icon-button.js.map
